import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { UsersThree, PaperPlaneTilt, Trash, CheckCircle, Info } from "@phosphor-icons/react";

export default function TeamTab() {
  const t = useT();
  const tRef = useRef(t);
  tRef.current = t;
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null); // {ok, text}
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const isShared = !!localStorage.getItem("rg_workspace");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const r = await api.get("/team");
      setMembers(r.data || []);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setLoadError(typeof detail === "string" && detail.trim() ? detail : tRef.current("team.load_error"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function invite() {
    if (!email.trim()) return;
    setSending(true);
    setMsg(null);
    try {
      await api.post("/team/invite", { email: email.trim(), role });
      setMsg({ ok: true, text: t("team.invited") });
      setEmail("");
      await load();
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e.response?.data?.detail) });
    } finally { setSending(false); }
  }

  async function changeRole(m, newRole) {
    await api.put(`/team/${m.id}`, { role: newRole });
    await load();
  }

  async function remove(m) {
    if (!window.confirm(t("qms.confirm_delete"))) return;
    await api.delete(`/team/${m.id}`);
    await load();
  }

  return (
    <div className="space-y-5" data-testid="team-tab">
      <div className="grid-card p-5">
        <div className="inline-flex items-center gap-2">
          <UsersThree size={20} weight="duotone" className="text-blue-700" />
          <h2 className="text-lg font-extrabold tracking-tight">{t("team.title")}</h2>
        </div>
        <p className="text-sm text-zinc-600 mt-1">{t("team.sub")}</p>
        <div className="mt-3 inline-flex items-start gap-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 px-3 py-2 rounded-sm">
          <Info size={14} weight="fill" className="text-blue-600 shrink-0 mt-0.5" />
          <span>{t("team.hint")}</span>
        </div>
      </div>

      {isShared ? (
        <div className="grid-card p-5 text-sm text-zinc-600" data-testid="team-shared-note">{t("team.not_owner")}</div>
      ) : (
        <>
          {loadError && (
            <div className="grid-card flex flex-wrap items-center justify-between gap-3 border-amber-300 p-4 text-sm text-amber-900" role="alert" data-testid="team-load-error">
              <span>{loadError}</span>
              <button onClick={load} className="btn-primary px-3 py-2 text-xs" data-testid="team-load-retry">
                {t("app.retry")}
              </button>
            </div>
          )}
          <div className="grid-card p-4">
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">{t("team.invite_title")}</div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[220px]">
                <label className="text-xs font-semibold uppercase text-zinc-600">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="colega@empresa.com"
                  data-testid="team-invite-email"
                  className="mt-1 w-full border border-zinc-200 px-2 py-2 rounded-sm text-sm focus:border-blue-600 focus:outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-zinc-600">{t("team.role_label")}</label>
                <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="team-invite-role"
                  className="mt-1 border border-zinc-200 px-2 py-2 rounded-sm text-sm bg-white focus:border-blue-600 focus:outline-none">
                  <option value="viewer">{t("team.role.viewer")}</option>
                  <option value="editor">{t("team.role.editor")}</option>
                </select>
              </div>
              <button onClick={invite} disabled={sending || !email.trim()} data-testid="team-invite-btn"
                className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-40">
                <PaperPlaneTilt size={14} weight="bold" /> {sending ? "…" : t("team.invite_btn")}
              </button>
            </div>
            {msg && (
              <div className={`mt-3 inline-flex items-center gap-1 text-sm ${msg.ok ? "text-emerald-700" : "text-red-600"}`} data-testid="team-msg">
                {msg.ok && <CheckCircle size={14} weight="fill" />} {msg.text}
              </div>
            )}
          </div>

          <div className="grid-card p-0 overflow-x-auto">
            {loading ? (
              <div className="text-xs text-zinc-500 font-mono py-8 text-center">{t("dash.loading")}</div>
            ) : loadError ? null : members.length === 0 ? (
              <div className="text-xs text-zinc-500 font-mono py-8 text-center" data-testid="team-empty">{t("team.empty")}</div>
            ) : (
              <table className="w-full text-sm" data-testid="team-list">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                    <Th>Email</Th>
                    <Th>{t("team.role_label")}</Th>
                    <Th>{t("team.status")}</Th>
                    <Th>{t("team.invited_on")}</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`team-row-${m.id}`}>
                      <td className="px-3 py-2 font-semibold">
                        {m.member_email || <span className="text-amber-700">{t("team.email_missing")}</span>}
                      </td>
                      <td className="px-3 py-2">
                        <select value={m.role} onChange={(e) => changeRole(m, e.target.value)} data-testid={`team-role-${m.id}`}
                          disabled={m.needs_review}
                          className="border border-zinc-200 px-2 py-1 rounded-sm text-xs bg-white focus:border-blue-600 focus:outline-none disabled:opacity-50">
                          <option value="viewer">{t("team.role.viewer")}</option>
                          <option value="editor">{t("team.role.editor")}</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block border rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${m.registered ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                          {m.needs_review ? t("team.needs_review") : m.registered ? t("team.st_active") : t("team.st_pending")}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{(m.created_at || "").slice(0, 10)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end">
                          <button onClick={() => remove(m)} title={t("qms.delete")} data-testid={`team-del-${m.id}`}
                            className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm"><Trash size={14} weight="bold" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500 font-semibold">{children}</th>;
}
